/**
 * Live sync client for vfs-service's per-project tree WebSocket
 * (`ws(s)://<vfs-service host>/ws/v1/projects/:projectId/tree`) — the piece
 * that keeps a vfs-backed project's IndexedDB cache (see vfsClient.js's
 * module doc) from going stale while the browser is closed, or while a file
 * changes on vfs-service without ever being opened in Monaco in this tab.
 *
 * The socket sends `tree:init` (a full tree snapshot) immediately on every
 * connect, and one of `tree:created` / `doc:flushed` / `tree:deleted` /
 * `tree:moved` / `tree:bash_executed` after every subsequent mutation — see
 * vfs-service's src/ws/treeSocket.js. All paths in these messages are
 * PROJECT-relative (`<serviceName>/...`), not service-relative, since
 * vfs-service stores every service under its own subfolder of one project
 * repo — this module strips that prefix the same way vfsClient.js's
 * fetchServiceFiles does, and drops any event for a path outside the
 * service this connection was opened for (a project can hold more than one
 * service; only one is loaded into this tab's IndexedDB namespace).
 *
 * `tree:init` and `tree:bash_executed` map to `onFullResync(type, data)`
 * (reconnecting is itself the "resync on reopen" moment). A newer
 * vfs-service's `tree:bash_executed` says which project paths the command
 * changed (`data.paths`, null when too many): the handler can then update
 * only those. Everything else maps to a single-path
 * `onFileChanged`/`onFileDeleted` callback. `tree:repo_flags` (a repo flagged
 * Read-only or No build, or unflagged) goes to utils/repoFlags.js, not to the
 * handlers.
 */

import { publishRepoFlags } from './repoFlags';

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 10000;

function toWsUrl(httpBaseUrl, projectId) {
  const wsBase = httpBaseUrl.replace(/\/$/, '').replace(/^http/i, 'ws');
  return `${wsBase}/ws/v1/projects/${encodeURIComponent(projectId)}/tree`;
}

function stripServicePrefix(serviceName, projectRelativePath) {
  const prefix = `${serviceName}/`;
  if (!projectRelativePath || !projectRelativePath.startsWith(prefix)) {
    return null;
  }
  return projectRelativePath.slice(prefix.length);
}

/**
 * Opens (and keeps alive) a live sync connection for the WHOLE project (no
 * service-prefix stripping/filtering) — every path handed to the callbacks
 * is exactly as vfs-service's tree socket sent it, project-root-relative.
 * `connectProjectTreeSync` below is a thin service-scoped wrapper over this;
 * this raw variant exists for consumers that browse/edit the full project
 * tree directly (e.g. ProjectFilesBrowser.jsx), not one service's subfolder.
 * Returns a `disconnect()` function — call it on unmount / project change.
 */
export function connectProjectTreeSyncRaw(vfsServiceUrl, projectId, handlers) {
  const { onFullResync, onPathChanged, onPathDeleted, onPathMoved, onGitEvent } = handlers;
  let ws = null;
  let stopped = false;
  let reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
  let reconnectTimer = null;

  const handleMessage = (event) => {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }
    const { type, data } = message;

    // `git:pushed` (a push from a clone to Flow's git server): only for who asked (the Git tab).
    if (typeof type === 'string' && type.startsWith('git:')) {
      if (onGitEvent) onGitEvent(type, data);
      return;
    }
    // The project's repo flags changed (Read-only / No build): every view of them follows (utils/repoFlags.js).
    if (type === 'tree:repo_flags') {
      publishRepoFlags(projectId, data?.repos);
      return;
    }
    if (type === 'tree:init' || type === 'tree:bash_executed') {
      onFullResync(type, data);
      return;
    }
    if (type === 'tree:created' || type === 'doc:flushed') {
      if (data?.path) onPathChanged(data.path);
      return;
    }
    if (type === 'tree:deleted') {
      if (data?.path) onPathDeleted(data.path);
      return;
    }
    if (type === 'tree:moved') {
      if (data?.from || data?.to) onPathMoved(data.from, data.to);
    }
  };

  const connect = () => {
    if (stopped) return;
    ws = new WebSocket(toWsUrl(vfsServiceUrl, projectId));

    ws.onopen = () => {
      reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
    };
    ws.onmessage = handleMessage;
    ws.onerror = () => {
      // onclose fires right after in the browser WS spec — reconnect is handled there.
    };
    ws.onclose = () => {
      if (stopped) return;
      reconnectTimer = setTimeout(connect, reconnectDelay);
      reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
    };
  };

  connect();

  return function disconnect() {
    stopped = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (ws) {
      ws.onclose = null; // don't reconnect on an intentional close
      ws.close();
      ws = null;
    }
  };
}

/**
 * Opens (and keeps alive) a live sync connection for `serviceName` within
 * vfs-service project `projectId`. Returns a `disconnect()` function — call
 * it on unmount / when the active project or service changes.
 */
export function connectProjectTreeSync(vfsServiceUrl, projectId, serviceName, handlers) {
  const { onFullResync, onFileChanged, onFileDeleted } = handlers;
  return connectProjectTreeSyncRaw(vfsServiceUrl, projectId, {
    onFullResync,
    onPathChanged: (path) => {
      const relativePath = stripServicePrefix(serviceName, path);
      if (relativePath) onFileChanged(relativePath);
    },
    onPathDeleted: (path) => {
      const relativePath = stripServicePrefix(serviceName, path);
      if (relativePath) onFileDeleted(relativePath);
    },
    onPathMoved: (from, to) => {
      const fromRelative = stripServicePrefix(serviceName, from);
      const toRelative = stripServicePrefix(serviceName, to);
      if (fromRelative && toRelative) {
        onFileDeleted(fromRelative);
        onFileChanged(toRelative);
      } else if (toRelative) {
        onFileChanged(toRelative);
      } else if (fromRelative) {
        onFileDeleted(fromRelative);
      }
    },
  });
}

export default { connectProjectTreeSync, connectProjectTreeSyncRaw };
